import React, { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * Convert any Instagram link (post, reel, tv) to its embed URL.
 * Supports formats: /reel/{id}, /p/{id}, /tv/{id} with or without trailing slash and ?queries.
 */
export function instagramEmbedUrl(rawUrl) {
  if (!rawUrl) return null;
  try {
    const u = new URL(rawUrl);
    if (!/instagram\.com$/i.test(u.hostname.replace("www.", ""))) return null;
    const m = u.pathname.match(/^\/(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/i);
    if (!m) return null;
    const kind = m[1].toLowerCase().startsWith("reel") ? "reel" : (m[1].toLowerCase() === "tv" ? "tv" : "p");
    return `https://www.instagram.com/${kind}/${m[2]}/embed/`;
  } catch {
    return null;
  }
}

export default function VideoModal({ video, onClose }) {
  const overlayRef = useRef(null);

  // ESC closes; lock background scroll while open
  useEffect(() => {
    if (!video) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [video, onClose]);

  if (!video) return null;
  const embed = instagramEmbedUrl(video.video_url);

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm grid place-items-center px-3"
      role="dialog"
      aria-modal="true"
      data-testid="video-modal"
    >
      <div className="relative bg-black w-full max-w-md rounded-lg overflow-hidden shadow-2xl">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 z-10 p-2 rounded-full bg-black/60 text-white hover:bg-black/80 transition"
          aria-label="Close"
          data-testid="video-modal-close"
        >
          <X size={18} />
        </button>
        {embed ? (
          <iframe
            title={video.title || "Impact video"}
            src={embed}
            width="100%"
            height="640"
            frameBorder="0"
            scrolling="no"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full bg-black"
            data-testid="video-modal-iframe"
          />
        ) : (
          <div className="p-8 text-center text-white/80">
            <p>Unable to embed this video.</p>
            <a href={video.video_url} target="_blank" rel="noreferrer" className="text-[var(--bb-terracotta)] underline mt-3 inline-block">
              Open on Instagram →
            </a>
          </div>
        )}
        {video.title && (
          <div className="p-4 text-white/85 text-sm border-t border-white/10">{video.title}</div>
        )}
      </div>
    </div>
  );
}
